import { RecurringUnit } from "protogen/calendar_pb";
import {
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
} from "protogen/calendar_service_pb";
import { RichContent } from "components/editor/utils";
import {
  CalendarEvent,
  EventReminder,
  EventNotice,
  EventNotice_NoticeType,
} from "protogen/calendar_pb";
import { VideoRoom } from "protogen/video_pb";
import { attachmentsToUploads } from "components/creation/FileUploader";
import { Attachment } from "protogen/common_pb";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";

type EphemeralEvent = {
  title?: string;
  description?: RichContent;
  allDay?: boolean;
  startDate?: Date;
  endDate?: Date;
  invitees?: string[];
  recurringInterval?: number;
  recurringUnit?: RecurringUnit;
  recurringUntil?: Date;
  // Advanced recurrence fields
  recurringByDay?: number[]; // Days of week [0-6] for Sun-Sat
  recurringCount?: number; // End after X occurrences
  recurringByMonthDay?: number[]; // Days of month [1-31]
  recurringBySetPos?: number; // E.g., 2 for "2nd Tuesday"
  // Only matters if family selector is present.
  familyRef?: string;
  // Only needed for form control
  isRecurring?: boolean;
  initialAttachments?: Attachment[];
  initialDetailsText?: string;
  // Used for associating a task with where it was made.
  sourceEntityType?: string;
  sourceEntityRef?: string;
  sourceEntityIndex?: number;
  reminders?: EventReminder[];
  displayTimezone?: string;
  location?: string;
  isRecurringSingleInstance?: boolean;
  transparency?: string;
  visibility?: string;
  videoRoom?: VideoRoom;
  targetIntegrationRef?: string;
  targetCalendarId?: string;
  notifyCalendarOwner?: boolean;
};

const DEFAULT_EVENT_DURATION_MS = 60 * 60 * 1000; // 1 hour

const createDefaultEphemeralEvent = (): EphemeralEvent => ({
  title: "",
  description: {
    text: "",
    json: "",
    attachments: [],
    html: "",
  },
  allDay: false,
  startDate: getNextHourDate(),
  endDate: new Date(getNextHourDate().getTime() + DEFAULT_EVENT_DURATION_MS),
  invitees: [],
  recurringInterval: 0,
  recurringUnit: RecurringUnit.RecurringUnit_UNSPECIFIED,
  recurringUntil: new Date(),
  familyRef: "",
  isRecurring: false,
  initialAttachments: [],
  reminders: undefined,
  displayTimezone: "",
  transparency: "opaque",
  visibility: "default",
  notifyCalendarOwner: true,
});

const getNextHourDate = (): Date => {
  const now = new Date();
  if (now.getMinutes() > 0) {
    now.setHours(now.getHours() + 1);
  }
  now.setMinutes(0, 0, 0);
  return now;
};

type FormErrors = { [key: string]: string };

const validateEvent = (event: EphemeralEvent): FormErrors => {
  const errors: FormErrors = {};
  // if (!event.familyRef) {
  //   errors["familyRef"] = "Required";
  // }
  if (!event.title) {
    errors["title"] = "Required";
  }
  if (!event.startDate) {
    errors["startDate"] = "Required";
  }

  if (event.startDate && event.endDate && event.startDate > event.endDate) {
    errors["startDate"] = "Start date must be before end date";
    errors["endDate"] = "End date must be after start date";
  }

  if (event.isRecurring) {
    if (!event.recurringInterval) {
      errors["recurringInterval"] = "Required";
    }
    if (!event.recurringUnit) {
      errors["recurringUnit"] = "Required";
    }
  }

  if (
    event.allDay &&
    event.displayTimezone &&
    (!event.recurringByDay || event.recurringByDay.length === 0)
  ) {
    errors["displayTimezone"] =
      "All day events cannot specify a timezone unless repeating by day of week";
  }

  return errors;
};

const formattedDate = (date: Date) =>
  `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

const toDate = (dateString: string) => {
  const [year, month, day] = dateString.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const buildRequest = (
  event: EphemeralEvent,
  originalEvent?: CalendarEvent,
  notice?: EventNotice,
): CreateCalendarEventRequest => {
  let startDate = event.startDate;
  let endDate = event.endDate;
  let startDisplayDate = "";
  let endDisplayDate = "";
  if (event.allDay) {
    startDisplayDate = formattedDate(startDate!);
    startDate!.setUTCHours(0, 0, 0, 0);
    if (endDate) {
      endDisplayDate = formattedDate(endDate!);
      endDate.setUTCHours(23, 59, 59, 999);
    }
  }

  let recurringUntilDate = event.recurringUntil;
  if (event.displayTimezone) {
    if (startDate) {
      startDate = zonedTimeToUtc(startDate, event.displayTimezone);
    }
    if (endDate) {
      endDate = zonedTimeToUtc(endDate, event.displayTimezone);
    }
    if (recurringUntilDate) {
      recurringUntilDate = zonedTimeToUtc(
        recurringUntilDate,
        event.displayTimezone,
      );
    }
  }

  return new CreateCalendarEventRequest({
    title: event.title || "",
    description: {
      payload: event?.description?.html || "",
      contentType: "html",
      textContent: event?.description?.text || "",
      attachments: event?.description?.attachments || [],
    },
    allDay: !!event.allDay,
    startSecUTC: BigInt(Math.ceil((startDate?.getTime() || 0) / 1000)),
    endSecUTC: BigInt(Math.ceil((endDate?.getTime() || 0) / 1000)),
    startDisplayDate: startDisplayDate,
    endDisplayDate: endDisplayDate,
    recurringInterval: event.isRecurringSingleInstance
      ? 0
      : event.isRecurring
        ? event.recurringInterval || 0
        : 0,
    recurringUnit: event.isRecurringSingleInstance
      ? RecurringUnit.RecurringUnit_UNSPECIFIED
      : event.isRecurring
        ? event.recurringUnit
        : RecurringUnit.RecurringUnit_UNSPECIFIED,
    recurringUntilSecUTC: BigInt(
      Math.ceil((recurringUntilDate?.getTime() || 0) / 1000),
    ),
    invitees: event.invitees || [],
    familyRef: event.familyRef || "",
    sourceEntityType: event.sourceEntityType || "",
    sourceEntityRef: event.sourceEntityRef || "",
    sourceEntityIndex: event.sourceEntityIndex || 0,
    reminders: event.reminders,
    displayTimezone: event.displayTimezone || "",
    location: event.location || "",
    isRecurringSingleInstance: event.isRecurringSingleInstance,
    originalEventRef: event.isRecurringSingleInstance ? originalEvent?.ref : "",
    originalEventStartSec: event.isRecurringSingleInstance
      ? notice?.startSec
      : undefined,
    transparency: event.transparency,
    visibility: event.visibility,
    videoRef: event.videoRoom?.ref || "",
    targetIntegrationRef: event.targetIntegrationRef || "",
    targetCalendarId: event.targetCalendarId || "",
    notifyCalendarOwner: event.notifyCalendarOwner ?? true,
    // Advanced recurrence fields
    recurringByDay: event.recurringByDay || [],
    recurringCount: event.recurringCount || 0,
    recurringByMonthDay: event.recurringByMonthDay || [],
    recurringBySetPos: event.recurringBySetPos || 0,
  });
};

const buildUpdateRequest = (
  eventRef: string,
  event: CalendarEvent | undefined,
  formData: EphemeralEvent,
): UpdateCalendarEventRequest => {
  const startDate = formData.startDate;
  const endDate = formData.endDate;
  let startDisplayDate = "";
  let endDisplayDate = "";
  if (formData.allDay) {
    startDisplayDate = formattedDate(startDate!);
    startDate!.setUTCHours(0, 0, 0, 0);
    if (endDate) {
      endDisplayDate = formattedDate(endDate!);
      endDate.setUTCHours(23, 59, 59, 999);
    }
  }

  const hasRepeatChanged = event
    ? event.recurringInterval !== formData.recurringInterval ||
      event.recurringUnit !== formData.recurringUnit ||
      JSON.stringify(event.recurringByDay) !==
        JSON.stringify(formData.recurringByDay) ||
      event.recurringCount !== formData.recurringCount ||
      JSON.stringify(event.recurringByMonthDay) !==
        JSON.stringify(formData.recurringByMonthDay) ||
      event.recurringBySetPos !== formData.recurringBySetPos ||
      Number(event.recurringUntilSec) !==
        (formData.recurringUntil?.getTime() || 0) / 1000
    : true; // For external events, always send the repeat fields

  const conversionTimezone =
    event?.displayTimezone || formData.displayTimezone || "";
  const startDateUTC =
    formData.displayTimezone && formData.startDate
      ? zonedTimeToUtc(formData.startDate, conversionTimezone)
      : formData.startDate;

  const endDateUTC =
    formData.displayTimezone && formData.endDate
      ? zonedTimeToUtc(formData.endDate, conversionTimezone)
      : formData.endDate;
  let updatedDescription = undefined;
  if (
    event?.details?.payload !== formData.description?.json ||
    event?.details?.textContent !== formData.description?.text ||
    JSON.stringify(event?.details?.attachments) !==
      JSON.stringify(formData.description?.attachments)
  ) {
    updatedDescription = {
      payload: formData.description?.html || "",
      contentType: "html",
      textContent: formData.description?.text || "",
      attachments: formData.description?.attachments || [],
    };
  }
  const displayTimezoneChanged =
    event?.displayTimezone !== formData.displayTimezone;

  return new UpdateCalendarEventRequest({
    eventRef: eventRef,
    updatedTitle: event?.title === formData.title ? "" : formData.title,
    updatedDescription: updatedDescription,
    updatedAllDay: formData.allDay,
    updatedStartSecUTC:
      event &&
      Number(event.startSec) === (startDateUTC?.getTime() || 0) / 1000 &&
      !displayTimezoneChanged
        ? undefined
        : BigInt(Math.ceil((startDateUTC?.getTime() || 0) / 1000)),
    updatedEndSecUTC:
      event &&
      Number(event.endSec) === (endDateUTC?.getTime() || 0) / 1000 &&
      !displayTimezoneChanged
        ? undefined
        : BigInt(Math.ceil((endDateUTC?.getTime() || 0) / 1000)),
    updatedStartDisplayDate: startDisplayDate,
    updatedEndDisplayDate: endDisplayDate,
    updatedRecurringInterval: !hasRepeatChanged
      ? undefined
      : formData.recurringInterval,
    updatedRecurringUnit: !hasRepeatChanged
      ? undefined
      : formData.recurringUnit || RecurringUnit.RecurringUnit_UNSPECIFIED,
    updatedRecurringUntilSecUTC:
      event &&
      Number(event.recurringUntilSec) ===
        (formData.recurringUntil?.getTime() || 0) / 1000
        ? undefined
        : BigInt(Math.ceil((formData.recurringUntil?.getTime() || 0) / 1000)),
    updatedRecurringByDay: !hasRepeatChanged
      ? undefined
      : formData.recurringByDay,
    updatedRecurringCount: !hasRepeatChanged
      ? undefined
      : formData.recurringCount,
    updatedRecurringByMonthDay: !hasRepeatChanged
      ? undefined
      : formData.recurringByMonthDay,
    updatedRecurringBySetPos: !hasRepeatChanged
      ? undefined
      : formData.recurringBySetPos,
    shouldUpdateRecurringByDay: hasRepeatChanged,
    shouldUpdateRecurringCount: hasRepeatChanged,
    shouldUpdateRecurringByMonthDay: hasRepeatChanged,
    shouldUpdateRecurringBySetPos: hasRepeatChanged,
    updatedInvitees:
      event &&
      event.attendees
        .map((a) => {
          return a.email;
        })
        .sort() === formData.invitees?.sort()
        ? undefined
        : formData.invitees,
    updatedReminders: formData.reminders,
    updatedDisplayTimezone: displayTimezoneChanged
      ? formData.displayTimezone
      : "",
    shouldUpdateDisplayTimezone: displayTimezoneChanged,
    shouldUpdateLocation: event?.location !== formData.location,
    updateLocation: formData.location,
    shouldUpdateAllDay: event?.allDay !== formData.allDay,
    updatedTransparency:
      event?.transparency === formData.transparency
        ? undefined
        : formData.transparency,
    updatedVisibility:
      (event?.visibility || "default") === formData.visibility
        ? undefined
        : formData.visibility,
    updatedVideoRef:
      event?.videoRoom?.ref === formData.videoRoom?.ref
        ? undefined
        : formData.videoRoom?.ref,
    targetIntegrationRef: formData.targetIntegrationRef,
    targetCalendarId: formData.targetCalendarId,
    updateAllInstances: !formData.isRecurringSingleInstance,
    notifyCalendarOwner: formData.notifyCalendarOwner ?? true,
  });
};

const buildEphemeralEventFromEventNotice = (
  notice: EventNotice,
): EphemeralEvent => {
  const isAllDay = notice.allDay;
  const isRecurring = notice.recurringInterval > 0;
  return {
    title: notice.title,
    description: {
      text: notice.textOnlyDetails || "",
      json: "",
      attachments: [],
      html: "",
    },
    initialDetailsText: notice.textOnlyDetails || "",
    allDay: isAllDay,
    startDate: isAllDay
      ? toDate(notice.startDate)
      : notice.displayTimezone
        ? utcToZonedTime(
            new Date(Number(notice.startSec) * 1000),
            notice.displayTimezone,
          )
        : new Date(Number(notice.startSec) * 1000),
    endDate: isAllDay
      ? toDate(notice.endDate || notice.startDate)
      : Number(notice.endSec) === 0
        ? undefined
        : notice.displayTimezone
          ? utcToZonedTime(
              new Date(Number(notice.endSec) * 1000),
              notice.displayTimezone,
            )
          : new Date(Number(notice.endSec) * 1000),
    invitees: notice.attendees.map((attendee) => attendee.email),
    recurringInterval: notice.recurringInterval,
    recurringUnit: notice.recurringUnit,
    recurringUntil:
      isRecurring && notice.recurringUntilSec
        ? new Date(Number(notice.recurringUntilSec) * 1000)
        : undefined,
    recurringByDay:
      notice.recurringByDay.length > 0 ? notice.recurringByDay : undefined,
    recurringCount:
      notice.recurringCount > 0 ? notice.recurringCount : undefined,
    recurringByMonthDay:
      notice.recurringByMonthDay.length > 0
        ? notice.recurringByMonthDay
        : undefined,
    recurringBySetPos:
      notice.recurringBySetPos !== 0 ? notice.recurringBySetPos : undefined,
    familyRef: notice.familyRef,
    isRecurring: isRecurring,
    initialAttachments: [],
    reminders: notice.reminders,
    displayTimezone:
      isAllDay && (!notice.recurringByDay || notice.recurringByDay.length === 0)
        ? ""
        : notice.displayTimezone,
    location: notice.locationString,
    isRecurringSingleInstance: true,
    transparency: notice.transparency || "opaque",
    visibility: notice.visibility || "default",
    videoRoom: notice.videoRoom,
    targetIntegrationRef: notice.externalCalendarRef || undefined,
    targetCalendarId: notice.externalCalendarId || undefined,
  };
};

const buildEphemeralEventFromCalendarEvent = (
  ce: CalendarEvent,
  notice?: EventNotice,
): EphemeralEvent => {
  const isAllDay = ce.allDay;
  const isRecurring = ce.recurringInterval > 0;
  // Only use external calendar info if this is actually an external event
  const isExternalEvent =
    notice?.noticeType === EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT;
  return {
    title: ce.title,
    description: {
      text: ce?.details?.textContent || "",
      json:
        ce?.details?.contentType === "json" ? ce?.details?.payload || "" : "",
      attachments: attachmentsToUploads(ce?.details?.attachments || []),
      html:
        ce?.details?.contentType === "html" ? ce?.details?.payload || "" : "",
    },
    allDay: isAllDay,
    startDate: isAllDay
      ? toDate(ce.startDisplayDate)
      : ce.displayTimezone
        ? utcToZonedTime(
            new Date(Number(notice?.startSec || ce.startSec) * 1000),
            ce.displayTimezone,
          )
        : new Date(Number(notice?.startSec || ce.startSec) * 1000),
    endDate: isAllDay
      ? toDate(ce.endDisplayDate || ce.startDisplayDate)
      : Number(notice?.endSec) === 0
        ? undefined
        : ce.displayTimezone
          ? utcToZonedTime(
              new Date(Number(notice?.endSec || ce.endSec) * 1000),
              ce.displayTimezone,
            )
          : new Date(Number(notice?.endSec || ce.endSec) * 1000),
    invitees: ce.attendees.map((attendee) => attendee.email),
    recurringInterval: ce.recurringInterval,
    recurringUnit: ce.recurringUnit,
    recurringUntil:
      isRecurring && ce.recurringUntilSec
        ? new Date(Number(ce.recurringUntilSec) * 1000)
        : undefined,
    recurringByDay:
      ce.recurringByDay.length > 0 ? ce.recurringByDay : undefined,
    recurringCount: ce.recurringCount > 0 ? ce.recurringCount : undefined,
    recurringByMonthDay:
      ce.recurringByMonthDay.length > 0 ? ce.recurringByMonthDay : undefined,
    recurringBySetPos:
      ce.recurringBySetPos !== 0 ? ce.recurringBySetPos : undefined,
    familyRef: ce.familyRef,
    isRecurring: isRecurring,
    initialAttachments: ce.details?.attachments || [],
    reminders: ce.reminders,
    displayTimezone:
      isAllDay && (!ce.recurringByDay || ce.recurringByDay.length === 0)
        ? ""
        : ce.displayTimezone,
    location: ce.location,
    isRecurringSingleInstance: isExternalEvent ? true : false,
    // External events read these live from the calendar; the local row can be stale.
    transparency: (isExternalEvent && notice?.transparency) || ce.transparency,
    visibility:
      (isExternalEvent && notice?.visibility) || ce.visibility || "default",
    videoRoom: ce.videoRoom,
    targetIntegrationRef: isExternalEvent
      ? notice?.externalCalendarRef || undefined
      : undefined,
    targetCalendarId: isExternalEvent
      ? notice?.externalCalendarId || undefined
      : undefined,
  };
};

const recurringUnitString = (unit: RecurringUnit) => {
  switch (unit) {
    case RecurringUnit.RecurringUnit_DAY:
      return "day";
    case RecurringUnit.RecurringUnit_WEEK:
      return "week";
    case RecurringUnit.RecurringUnit_MONTH:
      return "month";
    case RecurringUnit.RecurringUnit_YEAR:
      return "year";
    default:
      return "";
  }
};

type CalendarParameters = {
  allFamiliesSelected: boolean;
  familyRefs: string[] | null;
  ignoreTasks: boolean;
  ignoreEvents: boolean;
  includeOverdueTasks: boolean;
  startSec: bigint;
  endSec: bigint | null;
};

type CalendarParametersV2 = {
  allFamiliesSelected: boolean;
  familyRefs: string[] | null;
  ignoreTasks: boolean;
  ignoreEvents: boolean;
  includeOverdueTasks: boolean;
  month: number;
  year: number;
  day: number;
};

const todaySec = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return BigInt(Math.ceil((today?.getTime() || 0) / 1000));
};

const nowSec = () => {
  const today = new Date();
  return BigInt(Math.ceil((today?.getTime() || 0) / 1000));
};

const defaultParameters = (): CalendarParameters => {
  return {
    allFamiliesSelected: true,
    familyRefs: null,
    startSec: todaySec(),
    endSec: null,
    ignoreTasks: false,
    ignoreEvents: false,
    includeOverdueTasks: true,
  };
};
const defaultParametersV2 = (): CalendarParametersV2 => {
  return {
    allFamiliesSelected: true,
    familyRefs: null,
    ignoreTasks: false,
    ignoreEvents: false,
    includeOverdueTasks: true,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    day: new Date().getDay(),
  };
};

export type {
  EphemeralEvent,
  FormErrors,
  CalendarParameters,
  CalendarParametersV2,
};

export {
  validateEvent,
  buildRequest,
  recurringUnitString,
  defaultParameters,
  defaultParametersV2,
  todaySec,
  buildEphemeralEventFromCalendarEvent,
  buildEphemeralEventFromEventNotice,
  buildUpdateRequest,
  createDefaultEphemeralEvent,
  nowSec,
  DEFAULT_EVENT_DURATION_MS,
};
